import { shallowEqual } from 'react-redux'
import { createReduxModule } from 'hooks-for-redux'
import { ExternalIntegration } from 'lib/types'
import {
  getAllExternalIntegrations,
  getAllExternalIntegrationsByOrgID,
  getExternalIntegration,
  deleteExternalIntegration,
  upsertExternalIntegration,
} from './api'
import { RateLimitedTimer } from 'lib/utils'
import * as PopUpNotifications from './PopUpNotifications'
import { getRecordsById, getRecordsByOrgId } from './modelUtils'
import { NavState } from 'models'
import { ALL_ORGS_SELECTED } from 'lib/constants'
const deepEqual = require('fast-deep-equal')

interface ExternalIntegrationState {
  integrations: ExternalIntegration[]
  integrationsById: { [key: string]: ExternalIntegration }
  integrationsByOrgId: { [key: string]: ExternalIntegration[] }
  loading: boolean
  error?: any
}

const initialState: ExternalIntegrationState = {
  integrations: [],
  integrationsById: {},
  integrationsByOrgId: {},
  loading: true,
}

const setExternalIntegrationsReducer = (state: ExternalIntegrationState, integrations: ExternalIntegration[]) => ({
  ...state,
  integrations: (integrations || []).sort((a, b) => a.name.localeCompare(b.name)),
  integrationsById: getRecordsById<ExternalIntegration>(integrations),
  integrationsByOrgId: getRecordsByOrgId<ExternalIntegration>(integrations || []),
})

export const [
  use,
  { setExternalIntegrations, setOrgIntegrations, setLoading, clearLoading, setError, updateExternalIntegration },
  store,
] = createReduxModule('externalIntegrations', initialState, {
  setExternalIntegrations: setExternalIntegrationsReducer,
  setOrgIntegrations: (
    state: ExternalIntegrationState,
    payload: { orgId: string; integrations: ExternalIntegration[] },
  ): ExternalIntegrationState => {
    const { orgId, integrations: orgIntegrations } = payload
    const tagged = (orgIntegrations || []).map(i => ({ ...i, orgId: i.orgId ?? orgId }))
    const others = state.integrations.filter(i => i.orgId !== orgId)
    return setExternalIntegrationsReducer(state, [...others, ...tagged])
  },
  updateExternalIntegration: (
    state: ExternalIntegrationState,
    updatedItem: ExternalIntegration,
  ): ExternalIntegrationState => {
    if (!updatedItem.id) return state
    return deepEqual(updatedItem, state.integrationsById[updatedItem.id])
      ? state
      : setExternalIntegrationsReducer(
          state,
          state.integrations.find(item => item.id === updatedItem.id)
            ? state.integrations.map(item => (item.id === updatedItem.id ? updatedItem : item))
            : [...state.integrations, updatedItem],
        )
  },
  setLoading: (state: ExternalIntegrationState, loading: boolean) => ({ ...state, loading }),
  clearLoading: (state: ExternalIntegrationState) => ({ ...state, loading: false }),
  setError: (state: ExternalIntegrationState, error: any) => ({ ...state, error }),
})

export const useSelectedOrgsDevices = () => {
  const selectedOrgId = NavState.use(({ selectedOrgId }) => selectedOrgId)
  const { integrations, integrationsByOrgId } = use(({ integrations, integrationsByOrgId }) => {
    return { integrations, integrationsByOrgId }
  }, shallowEqual)
  return selectedOrgId === ALL_ORGS_SELECTED ? integrations : integrationsByOrgId[selectedOrgId] || []
}

const reloadRateLimiter = new RateLimitedTimer()
/* reload the specified org to get current stats; Note: rate-limited to one call per org per 5 seconds. */
export const reloadOrg = (orgId: string) =>
  reloadRateLimiter.timeout(process.env.NODE_ENV === 'test' ? 100 : 5000, orgId, () =>
    getExternalIntegration(orgId).then(updateExternalIntegration),
  )

export const setErrorWithNotificationAndRethrow = (error: any) => {
  clearLoading()
  if (error.response.status >= 500) {
    PopUpNotifications.fireErrorObject(error)
    setError(error)
    throw error
  }
}

/** Fetches the list of ExternalIntegration visible to the current user and stores them in the ExternalIntegrations H4R state. Super Admins can see all ExternalIntegrations. */
export const reload = () => {
  setLoading(true)
  return Promise.resolve()
    .then(getAllExternalIntegrations)
    .then(setExternalIntegrations)
    .then(clearLoading, setErrorWithNotificationAndRethrow)
}

/** Fetches external integrations scoped to a specific org (sends org_id header) and merges them into state. */
export const reloadForOrg = (orgId: string) => {
  if (!orgId || orgId === ALL_ORGS_SELECTED) return Promise.resolve()
  setLoading(true)
  return getAllExternalIntegrationsByOrgID(orgId)
    .then(integrations => setOrgIntegrations({ orgId, integrations }))
    .then(clearLoading, setErrorWithNotificationAndRethrow)
}

export type UpdateMessages = { success: string; error: string }

// First JSON:API error the server sent back (e.g. a validation message), if any.
const serverErrorDetail = (error: any): string | undefined => {
  const first = error?.response?.data?.errors?.[0]
  return first?.detail || first?.title
}

/**
 * Runs a write, then reloads. With `messages`, every outcome gets a pop-up:
 * success on resolve; on any failure (4xx, 5xx or no response at all) the
 * error message, followed by the server's detail when it sent one.
 * Failures are rethrown after being reported.
 */
const doUpdateAction = (f: any, messages?: UpdateMessages) => {
  setLoading(true)
  return Promise.resolve()
    .then(f)
    .then(
      result => {
        if (messages) PopUpNotifications.fireSuccess({ content: messages.success })
        reload()
        return result
      },
      error => {
        if (!messages) return setErrorWithNotificationAndRethrow(error)
        clearLoading()
        setError(error)
        const detail = serverErrorDetail(error)
        PopUpNotifications.fireError({ content: detail ? `${messages.error} ${detail}` : messages.error })
        throw error
      },
    )
}

/** Update an specific integration, them refresh the current H4R state. Super Admins can see all ExternalIntegrations. */
export const doUpsertExternalIntegration = (
  integration: ExternalIntegration,
  messages?: UpdateMessages,
): Promise<ExternalIntegration> =>
  // @ts-ignore
  doUpdateAction(() => upsertExternalIntegration(integration), messages)

export const deleteIntegration = (id: string, messages?: UpdateMessages) =>
  doUpdateAction(() => deleteExternalIntegration(id), messages)
